/**
 * Visitors: who arrives, where they walk, what they stop to look at, and what
 * they make of it.
 *
 * Pure logic. Nothing here imports `three`, the renderer or the DOM — it runs
 * over the small views below, which is why `tests/visitors.test.ts` can drive
 * a whole afternoon of arrivals in a millisecond with no scene at all. The
 * figures themselves are `src/models/visitors.ts`; the tuning and the wording
 * are `src/content/visitors.ts`.
 *
 * ## Why a view rather than the `Garden`
 *
 * `Garden` owns a scene graph, an audio engine and a particle system, none of
 * which a walking figure needs. Taking a flat description of the plot and its
 * plants instead keeps this module testable without a renderer and keeps it
 * liftable into the next project — the same split `src/progression/` makes
 * for the same reason (AGENTS.md, "Progression is a reusable engine plus this
 * game's content").
 *
 * ## The two rules that are not negotiable
 *
 * **A reaction is never a reward.** There is no progression import in this
 * file and there must never be one. See `content/visitors.ts`.
 *
 * **A visitor never stands where the player needs to tap.** Every position
 * this module produces is on a path threaded between bed rings and inside the
 * pavilion, so a figure can never cover a planting slot or a plant. The
 * figures are also not in the plot's pick tree, so they cannot intercept a
 * tap even when one passes over them — see `Garden.handleTap`.
 */

import { CROWD, VISITOR_KINDS, type ReactionId, type VisitorKind } from '../content/visitors';

export interface Point {
  x: number;
  z: number;
}

/** What a visitor needs to know about the ground it is walking on. */
export interface PlotView {
  /** Outer radius of the terrace, metres, in the plot's local space. */
  radius: number;
  /** Radius of the pavilion ring. Nothing walks across it except at a gate. */
  pavilionRadius: number;
  /** Angles, radians, where the pavilion ring can be walked through. */
  gateAngles: readonly number[];
  /** Every planting slot, whether or not something is growing in it. */
  slots: readonly Point[];
  /** Distinct bed-ring radii, innermost first. Paths thread between them. */
  ringRadii: readonly number[];
  /** Deck height at a distance from the centre — the districts are stepped. */
  groundYAt(radius: number): number;
}

/** What a visitor can see about one specimen. */
export interface PlantView {
  /** Which bed it stands in. The identity a pause holds on to. */
  slot: number;
  x: number;
  z: number;
  /** Species id. Only ever compared for distinctness. */
  speciesId: string;
  /** 0–1 toward maturity. */
  progress: number;
  mature: boolean;
  /** Rare or legendary. */
  rare: boolean;
  /** The weather left a mark on it. */
  mutated: boolean;
  /**
   * 0–1, how conspicuously this one has been left alone.
   *
   * This game has no thirst mechanic and must not grow one: growth is a
   * function of the clock and nothing may punish a player for being away
   * (AGENTS.md). So "neglect" here is only an *observation* — a seed that has
   * been in the ground a long while and never once been tended. It changes
   * nothing about the plant.
   */
  neglect: number;
}

export interface GardenView {
  plot: PlotView;
  plants: readonly PlantView[];
  /** Beds in the plot, planted or not. */
  capacity: number;
}

/* ------------------------------------------------------------------ *
 * Reactions
 * ------------------------------------------------------------------ */

/** Below this share of beds filled, the garden reads as empty. */
export const EMPTY_BELOW = 0.25;
/** At or above this share of beds filled, the garden reads as full. */
export const FULL_ABOVE = 0.75;
/** …and this share of what is planted has to be grown for delight. */
export const GROWN_ABOVE = 0.6;
/** …across at least this many distinct species. "Varied" needs a number. */
export const VARIETY_FOR_DELIGHT = 3;
/** Above this, a specimen is conspicuously untended. */
export const NEGLECT_ABOVE = 0.6;

/**
 * Pick the reaction for a pause, from the plant being looked at and from the
 * garden as a whole.
 *
 * Priority runs specific before general, which is the opposite of the order
 * the design note lists them in and is deliberate: a visitor standing in
 * front of a rare, weather-marked specimen should react to that specimen,
 * not to the average of the fifty-odd beds behind it. The whole-garden
 * readings are the fallback for an ordinary plant.
 *
 * Pure, total, and a function of nothing but its arguments — which is what
 * makes "the reaction is truthful about the garden" a thing a test can check.
 */
export function chooseReaction(view: GardenView, target: PlantView | null): ReactionId {
  if (target) {
    if (target.rare || target.mutated) return 'surprised';
    if (target.neglect > NEGLECT_ABOVE) return 'concerned';
  }

  const capacity = Math.max(1, view.capacity);
  const filled = view.plants.length / capacity;
  if (filled < EMPTY_BELOW) return 'unimpressed';

  const grown = view.plants.filter((p) => p.mature).length / Math.max(1, view.plants.length);
  const species = new Set(view.plants.map((p) => p.speciesId)).size;
  if (filled >= FULL_ABOVE && grown >= GROWN_ABOVE && species >= VARIETY_FOR_DELIGHT) {
    return 'delighted';
  }

  return 'pleased';
}

/* ------------------------------------------------------------------ *
 * Where it is safe to walk
 * ------------------------------------------------------------------ */

/**
 * A path closer to the centre than this is not worth having: at tier 1 and
 * above the middle of the plot is usually occupied by whatever the innermost
 * district puts there, and a figure circling a 10 cm ring around it looks like
 * a fault rather than a stroll.
 */
const MIN_WALK_RADIUS = 0.18;

/**
 * The radii a visitor may circle the garden on: threaded between bed rings,
 * clear of every slot, and inside the pavilion.
 *
 * Derived from the rings rather than authored, because the rings move when a
 * district opens and an authored number would silently start landing in a
 * bed. Returns innermost first; empty is a legitimate answer for a plot with
 * no room, and the caller must handle it.
 */
export function walkRadii(plot: PlotView, clearance = CROWD.clearance): number[] {
  const rings = [...plot.ringRadii].sort((a, b) => a - b);
  const outer = plot.pavilionRadius - clearance;
  const bounds = [0, ...rings, outer];
  const out: number[] = [];

  for (let i = 0; i < bounds.length - 1; i++) {
    const mid = (bounds[i] + bounds[i + 1]) / 2;
    if (mid < MIN_WALK_RADIUS || mid > outer) continue;
    if (rings.some((r) => Math.abs(r - mid) < clearance)) continue;
    out.push(mid);
  }
  return out;
}

/**
 * True when a figure standing here would be on a bed, on the pavilion ring,
 * or outside the garden altogether.
 *
 * Used by the tests as the statement of the rule; the itinerary builder is
 * what actually keeps it true, so this never has to be consulted per frame.
 */
export function blocked(plot: PlotView, p: Point, clearance = CROWD.clearance): boolean {
  for (const slot of plot.slots) {
    const dx = slot.x - p.x;
    const dz = slot.z - p.z;
    if (dx * dx + dz * dz < clearance * clearance) return true;
  }

  const r = Math.hypot(p.x, p.z);
  if (r <= plot.pavilionRadius - clearance) return false;

  // Outside the ring is only legitimate on the way in or out through a gate.
  const a = Math.atan2(p.z, p.x);
  const aperture = clearance * 2;
  return !plot.gateAngles.some((g) => Math.abs(angleDelta(a, g)) < aperture / plot.pavilionRadius);
}

/** Signed shortest angle from `b` to `a`, in (-PI, PI]. */
function angleDelta(a: number, b: number): number {
  let d = (a - b) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d <= -Math.PI) d += Math.PI * 2;
  return d;
}

/* ------------------------------------------------------------------ *
 * A visitor
 * ------------------------------------------------------------------ */

export type VisitorPhase = 'walking' | 'pausing' | 'gone';

interface Waypoint {
  x: number;
  z: number;
  /** The bed this waypoint stops to look at, or -1 for a plain step. */
  look: number;
}

export interface Visitor {
  readonly id: number;
  readonly kind: VisitorKind;
  x: number;
  z: number;
  /**
   * Yaw to give the figure, radians, in three.js's convention for a model
   * whose forward is +Z: `atan2(dirX, dirZ)`. The model layer applies it
   * straight to `rotation.y`, so the two never have to agree about anything
   * else.
   */
  heading: number;
  phase: VisitorPhase;
  /** Metres walked. Drives the leg swing, so it must never reset mid-visit. */
  distance: number;
  /** 0–1, how much of the walk cycle to show. Zero while stopped. */
  stride: number;
  /** The reaction currently above this visitor's head, if any. */
  reaction: ReactionId | null;
  /** 0–1 bubble opacity, faded in and out. */
  bubble: number;
  /** Ground height under the figure, metres. */
  y: number;
}

interface VisitorRuntime extends Visitor {
  route: Waypoint[];
  at: number;
  pauseLeft: number;
  /** Seconds left of the whole reaction beat, fade included. */
  showLeft: number;
}

/* ------------------------------------------------------------------ *
 * The crowd
 * ------------------------------------------------------------------ */

export class Crowd {
  private readonly people: VisitorRuntime[] = [];
  private readonly rng: () => number;
  private nextId = 1;
  private untilArrival: number;

  /**
   * `rng` is injected so a test can make a whole afternoon deterministic.
   * Production passes nothing and gets `Math.random`, because nothing about a
   * passer-by is worth seeding reproducibly — unlike a plant's jitter, which
   * is (see `models/plants.ts`).
   */
  constructor(rng: () => number = Math.random) {
    this.rng = rng;
    this.untilArrival = this.arrivalDelay() * this.rng();
  }

  /** Live visitors, for the renderer. Never longer than `maxConcurrent`. */
  get all(): readonly Visitor[] {
    return this.people;
  }

  get count(): number {
    return this.people.length;
  }

  /**
   * Advance the crowd.
   *
   * `dt` of zero or less is a no-op, which is how the app freezes visitors
   * while the page is hidden or the session is paused: no catch-up, no burst
   * of arrivals on return. A visitor is scenery, and scenery owes the player
   * nothing for the time they were away.
   */
  update(dt: number, view: GardenView): void {
    if (!(dt > 0)) return;

    for (let i = this.people.length - 1; i >= 0; i--) {
      this.advance(this.people[i], dt, view);
      if (this.people[i].phase === 'gone') this.people.splice(i, 1);
    }

    this.untilArrival -= dt;
    if (this.untilArrival > 0) return;
    this.untilArrival = this.arrivalDelay();

    if (this.people.length >= CROWD.maxConcurrent) return;
    if (view.plants.length < CROWD.minPlantsToVisit) return;
    const visitor = this.spawn(view);
    if (visitor) this.people.push(visitor);
  }

  /** Empty the crowd — the plot moved, or the garden was handed to a visit. */
  clear(): void {
    this.people.length = 0;
    this.untilArrival = this.arrivalDelay();
  }

  private arrivalDelay(): number {
    const jitter = (this.rng() * 2 - 1) * CROWD.arrivalJitter;
    return Math.max(1, CROWD.arrivalEverySeconds * (1 + jitter));
  }

  private pick<T>(list: readonly T[]): T {
    return list[Math.min(list.length - 1, Math.floor(this.rng() * list.length))];
  }

  private spawn(view: GardenView): VisitorRuntime | null {
    const radii = walkRadii(view.plot);
    if (radii.length === 0) return null;

    const targets = this.chooseTargets(view);
    const path = this.pathRadius(radii, targets);
    const entry = this.pick(view.plot.gateAngles);
    const exit = this.pick(view.plot.gateAngles);

    const route: Waypoint[] = [];
    const outside = view.plot.pavilionRadius + CROWD.approachMargin;
    const perimeter = radii[radii.length - 1];
    const push = (r: number, a: number, look = -1) =>
      route.push({ x: Math.cos(a) * r, z: Math.sin(a) * r, look });

    /*
     * In through the gate radially, but only as far as the *outermost* walk
     * ring — the one that lies beyond every bed. Running the entry radius all
     * the way to an inner path was the first version and it walked visitors
     * straight over the beds either side of the gate: a radial line crosses
     * every ring, and a gate angle is not chosen to miss them. Moving between
     * walk rings therefore happens at an angle picked for being clear, which
     * is what `crossing` is for.
     */
    push(outside, entry);
    push(perimeter, entry);

    let here = entry;
    if (path !== perimeter) {
      here = this.crossing(view.plot, path, perimeter, entry);
      arcTo(push, perimeter, entry, here);
      push(path, here);
    }

    for (const t of targets) {
      const a = Math.atan2(t.z, t.x);
      arcTo(push, path, here, a);
      push(path, a, t.slot);
      here = a;
    }

    if (path !== perimeter) {
      const out = this.crossing(view.plot, path, perimeter, exit);
      arcTo(push, path, here, out);
      push(perimeter, out);
      here = out;
    }

    arcTo(push, perimeter, here, exit);
    push(perimeter, exit);
    push(outside, exit);

    const kind = this.pick(VISITOR_KINDS);
    const start = route[0];
    return {
      id: this.nextId++,
      kind,
      x: start.x,
      z: start.z,
      y: view.plot.groundYAt(outside),
      heading: Math.atan2(-start.x, -start.z),
      phase: 'walking',
      distance: 0,
      stride: 1,
      reaction: null,
      bubble: 0,
      route,
      at: 1,
      pauseLeft: 0,
      showLeft: 0,
    };
  }

  /**
   * Which plants this visitor will stop at.
   *
   * Weighted toward the ones worth reacting to — a rare specimen, one the
   * weather marked, one that has been left a long time — because a visitor
   * who only ever stops at the nearest acacia makes the reactions look
   * random. Never an empty bed: the list is drawn from what is standing, so
   * there is no slot index here that nothing grows in.
   */
  private chooseTargets(view: GardenView): PlantView[] {
    const { min, max } = CROWD.pausesPerVisit;
    const want = Math.min(
      view.plants.length,
      min + Math.floor(this.rng() * (max - min + 1)),
    );
    if (want <= 0) return [];

    const pool = [...view.plants];
    const out: PlantView[] = [];
    while (out.length < want && pool.length > 0) {
      const weights = pool.map((p) => 1 + (p.rare ? 3 : 0) + (p.mutated ? 3 : 0) + p.neglect * 2);
      let roll = this.rng() * weights.reduce((a, b) => a + b, 0);
      let index = weights.length - 1;
      for (let i = 0; i < weights.length; i++) {
        roll -= weights[i];
        if (roll <= 0) {
          index = i;
          break;
        }
      }
      out.push(pool[index]);
      pool.splice(index, 1);
    }
    return out;
  }

  /**
   * An angle near `near` along which a visitor can step between two walk
   * rings without touching a bed.
   *
   * Beds sit ~0.47 m apart on every ring and a figure needs `clearance`, so
   * on any one ring most angles are clear; a line crossing several rings at
   * once is what makes this a search rather than a formula. The scan covers
   * the full circle, and falling back to `near` on a plot with no clear line
   * at all is deliberate — a visitor brushing a bed on a plot that tight is a
   * far smaller problem than one that never leaves the perimeter.
   */
  private crossing(plot: PlotView, from: number, to: number, near: number): number {
    const lo = Math.min(from, to);
    const hi = Math.max(from, to);
    const steps = Math.max(2, Math.ceil((hi - lo) / (CROWD.clearance * 0.8)));

    for (let k = 0; k <= 72; k++) {
      for (const sign of k === 0 ? [1] : [1, -1]) {
        const a = near + sign * k * 0.045;
        let clear = true;
        for (let i = 0; i <= steps && clear; i++) {
          const r = lo + ((hi - lo) * i) / steps;
          clear = !blocked(plot, { x: Math.cos(a) * r, z: Math.sin(a) * r });
        }
        if (clear) return a;
      }
    }
    return near;
  }

  /** The walkable ring that passes closest to what this visitor came to see. */
  private pathRadius(radii: number[], targets: PlantView[]): number {
    if (targets.length === 0) return radii[radii.length - 1];
    const mean =
      targets.reduce((sum, t) => sum + Math.hypot(t.x, t.z), 0) / targets.length;
    return radii.reduce((best, r) =>
      Math.abs(r - mean) < Math.abs(best - mean) ? r : best,
    );
  }

  private advance(v: VisitorRuntime, dt: number, view: GardenView): void {
    // The bubble lives on its own clock so it can fade out while walking on.
    if (v.showLeft > 0) {
      v.showLeft = Math.max(0, v.showLeft - dt);
      const fade = CROWD.bubbleFadeSeconds;
      const total = CROWD.pauseSeconds + fade;
      const shown = total - v.showLeft;
      v.bubble = Math.min(1, Math.min(shown, v.showLeft) / fade);
      if (v.showLeft === 0) {
        v.bubble = 0;
        v.reaction = null;
      }
    }

    if (v.pauseLeft > 0) {
      v.pauseLeft = Math.max(0, v.pauseLeft - dt);
      v.stride += (0 - v.stride) * Math.min(1, dt * 8);
      return;
    }

    let budget = v.kind.speed * dt;
    while (budget > 0) {
      const target = v.route[v.at];
      if (!target) {
        v.phase = 'gone';
        return;
      }

      const dx = target.x - v.x;
      const dz = target.z - v.z;
      const dist = Math.hypot(dx, dz);

      if (dist <= budget) {
        v.x = target.x;
        v.z = target.z;
        v.distance += dist;
        budget -= dist;
        v.at += 1;

        if (target.look >= 0) {
          this.beginPause(v, target.look, view);
          return;
        }
        continue;
      }

      v.x += (dx / dist) * budget;
      v.z += (dz / dist) * budget;
      v.distance += budget;
      v.heading = Math.atan2(dx, dz);
      budget = 0;
    }

    v.y = view.plot.groundYAt(Math.hypot(v.x, v.z));
    v.phase = 'walking';
    v.stride += (1 - v.stride) * Math.min(1, dt * 6);
  }

  /**
   * Stop, turn to the plant, and read the garden *now*.
   *
   * The reaction is chosen at this moment rather than when the route was
   * planned, so a plant that matured or was lifted while the visitor walked
   * over gets the honest answer. A bed emptied in the meantime falls back to
   * a reading of the garden as a whole rather than inventing a plant.
   */
  private beginPause(v: VisitorRuntime, slot: number, view: GardenView): void {
    const plant = view.plants.find((p) => p.slot === slot) ?? null;
    v.phase = 'pausing';
    v.pauseLeft = CROWD.pauseSeconds;
    v.showLeft = CROWD.pauseSeconds + CROWD.bubbleFadeSeconds;
    v.bubble = 0;
    v.reaction = chooseReaction(view, plant);
    if (plant) v.heading = Math.atan2(plant.x - v.x, plant.z - v.z);
    v.y = view.plot.groundYAt(Math.hypot(v.x, v.z));
  }
}

/**
 * Lay waypoints along an arc so a visitor curves around the garden instead of
 * cutting straight across it — and, more to the point, so that every step of
 * the journey stays on a radius proven clear of the beds. A straight chord
 * between two points on a walk ring dips inside it, and inside it is where
 * the plants are.
 */
function arcTo(
  push: (r: number, a: number) => void,
  radius: number,
  from: number,
  to: number,
): void {
  let sweep = (to - from) % (Math.PI * 2);
  if (sweep > Math.PI) sweep -= Math.PI * 2;
  if (sweep <= -Math.PI) sweep += Math.PI * 2;

  const steps = Math.max(1, Math.ceil(Math.abs(sweep) / 0.12));
  for (let i = 1; i <= steps; i++) push(radius, from + (sweep * i) / steps);
}
